import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faTimes, faImage, faCalendarPlus } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import { amenityCommonApi } from '../amenityCommonApi';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AmenityMasterList = ({ siteId, authUser }) => {
    const [amenities, setAmenities] = useState([]);
    const [amenityTypes, setAmenityTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [users, setUsers] = useState([]);

    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [selectedAmenityToBook, setSelectedAmenityToBook] = useState(null);
    const [bookingData, setBookingData] = useState({
        userId: '',
        bookingDate: '',
        startTime: '',
        endTime: '',
        guestCount: 1,
        notes: ''
    });

    const [formData, setFormData] = useState({
        amenityName: '',
        amenityTypeId: '',
        status: 'Available',
        capacity: '',
        isBookable: false,
        isPricingEnable: false,
        price: '',
        availableDays: [],
        startTime: '07:00',
        endTime: '22:00',
        description: '',
        image: '',
        images: [],
        timePeriod: 'Hourly',
        isActiveId: 1
    });

    useEffect(() => {
        fetchTypes();
        fetchAmenities();
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const isCommercial = window.location.pathname.includes('/zentcomm');
            const res = await amenityCommonApi.getUsers(isCommercial);
            if (res.success || res.isSuccess || res.IsSuccess) {
                const userList = res.data?.data || res.data || [];
                setUsers(Array.isArray(userList) ? userList : []);
            }
        } catch (error) {
            console.error('Failed to fetch users', error);
        }
    };

    const fetchTypes = async () => {
        try {
            const res = await amenityCommonApi.getAllAmenityTypes();
            if ((res.data?.isSuccess || res.data?.IsSuccess) && Array.isArray(res.data.data)) {
                // only active types for the dropdown
                setAmenityTypes(res.data.data.filter(t => t.isActiveId === 1));
            }
        } catch (error) {
            console.error('Failed to load amenity types', error);
        }
    };

    const fetchAmenities = async () => {
        setLoading(true);
        try {
            const res = await amenityCommonApi.getAllAmenities();
            if ((res.data?.isSuccess || res.data?.IsSuccess) && Array.isArray(res.data.data)) {
                const mapped = res.data.data.map(a => {
                    const extractedImage = (typeof a.Image === 'string' && a.Image.includes(',')) ? a.Image.split(',')[0] : (a.Image || a.image || '');
                    const extractedImages = a.Images || a.images || (typeof a.Image === 'string' && a.Image.includes(',') ? a.Image.split(',') : []);

                    return {
                        ...a,
                        image: extractedImage,
                        images: extractedImages,
                        isPricingEnable: a.IsPricingEnable !== undefined ? a.IsPricingEnable : (a.isPricingEnable || false),
                        price: a.Price !== undefined ? a.Price : (a.price || 0),
                        isBookableBool: (String(a.isbookable).toLowerCase() === "true" || a.isbookable === true)
                    };
                });
                // Sort by ID descending
                setAmenities(mapped.sort((a, b) => b.amenityId - a.amenityId));
            } else {
                setAmenities([]);
            }
        } catch (error) {
            toast.error('An error occurred while fetching amenities.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleDayToggle = (day) => {
        setFormData(prev => {
            const days = prev.availableDays.includes(day)
                ? prev.availableDays.filter(d => d !== day)
                : [...prev.availableDays, day];
            return { ...prev, availableDays: days };
        });
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const readFiles = files.map(file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        });

        Promise.all(readFiles)
            .then(base64Images => {
                setFormData(prev => ({
                    ...prev,
                    images: [...(prev.images || []), ...base64Images]
                }));
            })
            .catch(err => {
                console.error("Error reading files", err);
                toast.error("Failed to read image files.");
            });
    };

    const removeImage = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, index) => index !== indexToRemove)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.amenityName || !formData.amenityTypeId || !formData.capacity) {
            toast.error("Please fill all required fields.");
            return;
        }

        if (formData.isPricingEnable && (!formData.price || isNaN(formData.price))) {
            toast.error("Please enter a valid price when pricing is enabled.");
            return;
        }

        if (formData.availableDays.length === 0) {
            toast.error("Please select at least one available day.");
            return;
        }

        const selectedType = amenityTypes.find(t => t.amenityTypeId === Number(formData.amenityTypeId));

        // Format dates as required by backend (Date part is dummy, time part is used)
        const payload = {
            amenityId: isEditMode ? editId : 0,
            amenityName: formData.amenityName,
            amenityTypeId: Number(formData.amenityTypeId),
            amenityTypeName: selectedType ? selectedType.amenityTypeName : '',
            status: formData.status,
            capacity: Number(formData.capacity),
            isBookable: formData.isBookable,
            isPricingEnable: formData.isPricingEnable,
            price: formData.isPricingEnable ? Number(formData.price) : 0,
            timePeriod: formData.timePeriod || 'Hourly',
            availableDays: formData.availableDays.join(','),
            startTime: `2025-01-01T${formData.startTime}:00`,
            endTime: `2025-01-01T${formData.endTime}:00`,
            description: formData.description,
            image: formData.image,
            images: formData.images || [],
            isActiveId: formData.isActiveId,
            siteId: siteId,
            [isEditMode ? 'modifiedBy' : 'createdBy']: authUser?.email || String(siteId)
        };

        try {
            let res;
            if (isEditMode) {
                res = await amenityCommonApi.updateAmenity(editId, payload);
            } else {
                res = await amenityCommonApi.createAmenity(payload);
            }

            if (res.data?.isSuccess || res.data?.IsSuccess) {
                toast.success(res.data.message || res.data.Message || `Amenity ${isEditMode ? 'updated' : 'created'} successfully!`);
                setIsFormOpen(false);
                resetForm();
                fetchAmenities();
            } else {
                toast.error(res.data?.message || res.data?.Message || `Failed to ${isEditMode ? 'update' : 'create'} amenity.`);
            }
        } catch (error) {
            toast.error(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the amenity.`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this amenity?")) return;
        try {
            const res = await amenityCommonApi.deleteAmenity(id);
            if (res.data?.isSuccess || res.data?.IsSuccess) {
                toast.success(res.data.message || res.data.Message || "Amenity deleted successfully.");
                fetchAmenities();
            } else {
                toast.error(res.data?.message || res.data?.Message || "Failed to delete amenity.");
            }
        } catch (error) {
            toast.error("Error occurred while deleting the amenity.");
        }
    };

    const handleEdit = (row) => {
        setIsEditMode(true);
        setEditId(row.amenityId);

        // Extract HH:mm from backend's HH:mm:ss
        const formatTime = (timeStr) => (timeStr && timeStr.length >= 5) ? timeStr.substring(0, 5) : '00:00';

        setFormData({
            amenityName: row.amenityName || '',
            amenityTypeId: row.amenityTypeId || '',
            status: row.status || 'Available',
            capacity: row.capacity || '',
            isBookable: row.isBookableBool || String(row.isbookable).toLowerCase() === "true" || row.isbookable === true,
            isPricingEnable: row.isPricingEnable || false,
            price: row.price || '',
            availableDays: row.availableDays ? row.availableDays.split(',') : [],
            startTime: formatTime(row.startTime),
            endTime: formatTime(row.endTime),
            description: row.description || '',
            image: row.image || '',
            images: Array.isArray(row.images) ? row.images : [],
            timePeriod: row.timePeriod || 'Hourly',
            isActiveId: row.isActiveId !== undefined ? row.isActiveId : 1
        });
        setIsFormOpen(true);
    };

    const resetForm = () => {
        setIsEditMode(false);
        setEditId(null);
        setFormData({
            amenityName: '',
            amenityTypeId: '',
            status: 'Available',
            capacity: '',
            isBookable: false,
            isPricingEnable: false,
            price: '',
            availableDays: [],
            startTime: '07:00',
            endTime: '22:00',
            description: '',
            image: '',
            images: [],
            timePeriod: 'Hourly',
            isActiveId: 1
        });
    };

    const toggleForm = () => {
        if (isFormOpen) resetForm();
        setIsFormOpen(!isFormOpen);
    };

    const handleOpenBookingModal = (row) => {
        setSelectedAmenityToBook(row);
        setBookingData({
            userId: '',
            bookingDate: new Date().toISOString().split('T')[0],
            startTime: '09:00',
            endTime: '10:00',
            guestCount: 1,
            notes: ''
        });
        setIsBookingModalOpen(true);
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        
        if (!bookingData.userId) {
            toast.error("Please select a user.");
            return;
        }

        if (!bookingData.bookingDate || !bookingData.startTime || !bookingData.endTime) {
            toast.error("Please fill all time fields.");
            return;
        }

        const selectedUser = users.find(u => String(u.id || u.Id || u.userId) === String(bookingData.userId));
        const finalUnitId = selectedUser?.unitId || selectedUser?.UnitId || authUser?.unitId || 0;

        const payload = {
            bookingId: 0,
            amenityId: selectedAmenityToBook.amenityId,
            amenityName: selectedAmenityToBook.amenityName,
            unitId: finalUnitId,
            siteId: siteId,
            userId: Number(bookingData.userId),
            timeSlotId: 0,
            guestCount: Number(bookingData.guestCount),
            bookingDate: bookingData.bookingDate,
            startTime: `${bookingData.startTime}:00`,
            endTime: `${bookingData.endTime}:00`,
            bookingStatus: "Pending",
            createdBy: String(authUser?.personId || authUser?.id || "Admin"),
            isActiveId: 1
        };

        try {
            const res = await amenityCommonApi.createBooking(payload);
            if (res.data?.isSuccess || res.data?.IsSuccess) {
                toast.success(res.data.message || res.data.Message || "Booking created successfully!");
                setIsBookingModalOpen(false);
            } else {
                toast.error(res.data?.message || res.data?.Message || "Failed to create booking.");
            }
        } catch (error) {
            toast.error("Error creating booking.");
        }
    };

    const tableHeaders = [
        { label: "S. No", key: "sno", align: "center" },
        { label: "NAME", key: "amenityName", align: "left" },
        { label: "TYPE", key: "amenityType", align: "left" },
        { label: "CAPACITY", key: "capacity", align: "center" },
        { label: "PRICE", key: "price", align: "center" },
        { label: "STATUS", key: "status", align: "center" },
        { label: "BOOKABLE", key: "bookable", align: "center" },
        { label: "ACTIONS", key: "actionButtons", align: "center" },
    ];

    const renderCell = (key, row, header) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{amenities.indexOf(row) + 1}</span>;
            case "amenityName":
                return <span className="font-bold text-gray-800 tracking-tight">{row.amenityName}</span>;
            case "amenityType":
                return <span className="text-xs text-gray-500 font-medium">{row.amenityTypeName}</span>;
            case "capacity":
                return <span className="text-xs font-semibold">{row.capacity} Persons</span>;
            case "price":
                return (
                    <span className={`text-xs font-bold ${row.isPricingEnable && row.price > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                        {row.isPricingEnable && row.price > 0 ? `₹${row.price}` : 'Free'}
                    </span>
                );
            case "status":
                let statusColor = "bg-gray-100 text-gray-600";
                if (row.status === 'Available') statusColor = "bg-green-100 text-green-700";
                if (row.status === 'Closed') statusColor = "bg-red-100 text-red-700";
                if (row.status === 'Under Maintenance') statusColor = "bg-yellow-100 text-yellow-700";

                // Override if soft deleted
                if (row.isActiveId === 0) statusColor = "bg-gray-200 text-gray-500";
                const displayStatus = row.isActiveId === 0 ? 'Deleted' : row.status;

                return (
                    <span className={`px-2 py-1 rounded text-[10px] font-bold shadow-sm uppercase ${statusColor}`}>
                        {displayStatus}
                    </span>
                );
            case "bookable":
                const isBookable = row.isBookableBool || String(row.isbookable).toLowerCase() === "true" || row.isbookable === true;
                return (
                    <span className={`px-2 py-1 rounded text-[10px] font-bold ${isBookable ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                        {isBookable ? 'YES' : 'NO'}
                    </span>
                );
            case "actionButtons":
                const canBook = row.isBookableBool || String(row.isbookable).toLowerCase() === "true" || row.isbookable === true;
                return (
                    <div className="flex gap-3 justify-center">
                        {canBook && (
                            <button
                                onClick={() => handleOpenBookingModal(row)}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title="Book Amenity"
                            >
                                <FontAwesomeIcon icon={faCalendarPlus} />
                            </button>
                        )}
                        <button
                            onClick={() => handleEdit(row)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Edit"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                        {row.isActiveId !== 0 && (
                            <button
                                onClick={() => handleDelete(row.amenityId)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Delete"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        )}
                    </div>
                );
            default:
                return row[key] || "-";
        }
    };

    const expandedRowRender = (row) => {
        return (
            <div className="p-4 bg-gray-50 flex flex-col md:flex-row gap-6 border-b border-gray-200 items-start">
                <div className="flex gap-2 w-full md:w-auto overflow-x-auto flex-shrink-0">
                    {row.images && row.images.length > 0 ? (
                        row.images.map((imgUrl, idx) => (
                            <div key={idx} className="w-24 md:w-32 h-24 rounded bg-gray-200 flex items-center justify-center overflow-hidden border shrink-0">
                                <img src={imgUrl} alt={`Amenity ${idx + 1}`} className="w-full h-full object-cover" />
                            </div>
                        ))
                    ) : row.image ? (
                        <div className="w-24 md:w-32 h-24 rounded bg-gray-200 flex items-center justify-center overflow-hidden border shrink-0">
                            <img src={row.image} alt="Amenity" className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className="w-24 md:w-32 h-24 rounded bg-gray-200 flex flex-col items-center justify-center overflow-hidden border shrink-0 text-gray-400">
                            <FontAwesomeIcon icon={faImage} className="text-2xl mb-1" />
                        </div>
                    )}
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase">Description</h4>
                        <p className="text-sm text-gray-700 mt-1">{row.description || 'No description provided.'}</p>
                    </div>
                    <div>
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase">Available Days</h4>
                        <p className="text-sm font-semibold text-gray-800 mt-1">
                            {row.availableDays ? row.availableDays.split(',').join(', ') : 'Not set'}
                        </p>
                    </div>
                    <div>
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase">Operating Hours</h4>
                        <p className="text-sm font-semibold text-gray-800 mt-1">
                            {row.startTime ? row.startTime.substring(0, 5) : '-'} to {row.endTime ? row.endTime.substring(0, 5) : '-'}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">Amenities Master</h3>
                    <p className="text-sm text-gray-500">Manage individual amenities, rules, and pricing.</p>
                </div>
                <button
                    onClick={toggleForm}
                    className={`${isFormOpen ? 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2`}
                >
                    <FontAwesomeIcon icon={isFormOpen ? faTimes : faPlus} />
                    {isFormOpen ? 'Cancel' : 'Add Amenity'}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 animate-fade-in-down">
                    <h4 className="text-lg font-bold mb-4 text-gray-700">{isEditMode ? 'Edit Amenity' : 'Create Amenity'}</h4>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                        {/* Row 1 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Amenity Name *</label>
                            <input
                                type="text"
                                name="amenityName"
                                value={formData.amenityName}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Amenity Type *</label>
                            <select
                                name="amenityTypeId"
                                value={formData.amenityTypeId}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                                required
                            >
                                <option value="">Select Type</option>
                                {amenityTypes.map(type => (
                                    <option key={type.amenityTypeId} value={type.amenityTypeId}>{type.amenityTypeName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                                required
                            >
                                <option value="Available">Available</option>
                                <option value="Closed">Closed</option>
                                <option value="Under Maintenance">Under Maintenance</option>
                            </select>
                        </div>

                        {/* Row 2 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Capacity *</label>
                            <input
                                type="number"
                                name="capacity"
                                value={formData.capacity}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                required
                                min="1"
                            />
                        </div>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                                <input
                                    type="time"
                                    name="startTime"
                                    value={formData.startTime}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                                <input
                                    type="time"
                                    name="endTime"
                                    value={formData.endTime}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Time Period</label>
                            <select
                                name="timePeriod"
                                value={formData.timePeriod}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                            >
                                <option value="Hourly">Hourly</option>
                                <option value="Daily">Daily</option>
                                <option value="Half-Day">Half-Day</option>
                            </select>
                        </div>
                        <div className="md:col-span-2 lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Images (Base64)</label>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            />
                            {formData.images && formData.images.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {formData.images.map((imgSrc, idx) => (
                                        <div key={idx} className="relative w-16 h-16 border rounded overflow-hidden">
                                            <img src={imgSrc} alt="uploaded" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(idx)}
                                                className="absolute top-0 right-0 bg-red-500 text-white w-5 h-5 flex items-center justify-center rounded-bl"
                                            >
                                                <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Row 3 - Days */}
                        <div className="md:col-span-2 lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Available Days *</label>
                            <div className="flex flex-wrap gap-2">
                                {DAYS_OF_WEEK.map(day => (
                                    <button
                                        type="button"
                                        key={day}
                                        onClick={() => handleDayToggle(day)}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${formData.availableDays.includes(day)
                                                ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                                                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {day.substring(0, 3)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Toggles & Price */}
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="isBookable"
                                name="isBookable"
                                checked={formData.isBookable}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                            />
                            <label htmlFor="isBookable" className="text-sm font-medium text-gray-700 cursor-pointer">Allow Bookings</label>
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="isPricingEnable"
                                name="isPricingEnable"
                                checked={formData.isPricingEnable}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                            />
                            <label htmlFor="isPricingEnable" className="text-sm font-medium text-gray-700 cursor-pointer">Enable Pricing</label>
                        </div>

                        {formData.isPricingEnable && (
                            <div>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Price per slot..."
                                    required={formData.isPricingEnable}
                                    min="0"
                                />
                            </div>
                        )}

                        {/* Description */}
                        <div className="md:col-span-2 lg:col-span-3 mt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows="2"
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                placeholder="Rules, guidelines, or details..."
                            />
                        </div>

                        <div className="md:col-span-2 lg:col-span-3 mt-2 flex justify-end">
                            <button
                                type="submit"
                                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-8 rounded-lg transition-colors shadow"
                            >
                                {isEditMode ? 'Update Amenity' : 'Save Amenity'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <CommonTable
                    loading={loading}
                    tableHeading="All Amenities"
                    headers={tableHeaders}
                    data={amenities}
                    renderCell={renderCell}
                    expandedRowRender={expandedRowRender}
                    pagination={null} // showing all for simplicity
                />
            </div>

            {/* Booking Modal */}
            {isBookingModalOpen && selectedAmenityToBook && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fade-in-down">
                        <div className="flex justify-between items-center mb-4 border-b pb-3">
                            <h3 className="text-lg font-bold text-gray-800">
                                Book {selectedAmenityToBook.amenityName}
                            </h3>
                            <button onClick={() => setIsBookingModalOpen(false)} className="text-gray-400 hover:text-red-500">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleBookingSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select User *</label>
                                <select
                                    value={bookingData.userId}
                                    onChange={(e) => setBookingData({...bookingData, userId: e.target.value})}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                                    required
                                >
                                    <option value="">-- Select a User --</option>
                                    {users.map(u => (
                                        <option key={u.id || u.Id || u.userId} value={u.id || u.Id || u.userId}>
                                            {u.firstName || u.FirstName || u.name} {u.lastName || u.LastName || ''} - {u.email || u.Email || u.contactNo}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                                <input
                                    type="date"
                                    value={bookingData.bookingDate}
                                    onChange={(e) => setBookingData({...bookingData, bookingDate: e.target.value})}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                                    <input
                                        type="time"
                                        value={bookingData.startTime}
                                        onChange={(e) => setBookingData({...bookingData, startTime: e.target.value})}
                                        className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                                    <input
                                        type="time"
                                        value={bookingData.endTime}
                                        onChange={(e) => setBookingData({...bookingData, endTime: e.target.value})}
                                        className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Count *</label>
                                <input
                                    type="number"
                                    value={bookingData.guestCount}
                                    onChange={(e) => setBookingData({...bookingData, guestCount: e.target.value})}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                    required
                                    min="1"
                                    max={selectedAmenityToBook.capacity || 100}
                                />
                                <span className="text-[10px] text-gray-500">Max capacity: {selectedAmenityToBook.capacity}</span>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Instructions</label>
                                <textarea
                                    value={bookingData.notes}
                                    onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                                    rows="2"
                                    placeholder="Optional notes..."
                                />
                            </div>

                            {selectedAmenityToBook.isPricingEnable && (
                                <div className="bg-blue-50 p-3 rounded border border-blue-100 flex justify-between items-center">
                                    <span className="text-sm font-medium text-blue-800">Booking Price:</span>
                                    <span className="font-bold text-blue-700">₹{selectedAmenityToBook.price}</span>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 mt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsBookingModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded"
                                >
                                    Confirm Booking
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AmenityMasterList;
